import {
  DEV_PREVIEW_PLAY_INTEGRATED_API_BASE,
  type PlayIntegratedPreviewState,
} from '@/lib/dev-preview-play/integrated/constants'
import { INTEGRATED_PREVIEW_SHARE_USER_MESSAGE } from '@/lib/dev-preview-play/integrated/shareResponses'
import { PLAY_INTEGRATED_PRODUCTION_API_BASE } from '@/lib/play/playConnectConstants'
import { readPlayApiJsonSafely } from '@/lib/play/playApiResponse'
import {
  createPlayLoadError,
  isPlayDogAccessErrorCode,
  PLAY_LOAD_GENERIC_UNAVAILABLE,
  PLAY_LOAD_PREVIEW_UNAVAILABLE,
} from '@/lib/play/playLoadErrorMessage'
import {
  parsePreviewShareResponse,
  parseProductionShareResponse,
} from '@/lib/play/playIntegratedShareResolver'
import { normalizePlayIntegratedPendingPayload } from '@/lib/play/normalizePlayIntegratedPendingPayload'
import {
  circlePayloadFromTabSummary,
  tabSummaryHasPaintableCircle,
} from '@/lib/play/circlePayloadFromTabSummary'
import {
  markPlayStartup,
  markPlayStartupServerTimings,
} from '@/lib/play/playStartupTiming'
import type { PlayIntegratedCoordinationPreview } from '@/lib/dev-preview-play/integrated/coordinationFixture'
import type {
  PlayIntegratedCirclePayload,
  PlayIntegratedLoadHooks,
  PlayIntegratedLoadResult,
  PlayIntegratedPendingActionResult,
  PlayIntegratedPendingPayload,
  PlayIntegratedPreviewTabSummaryPayload,
  PlayIntegratedTabSummaryPayload,
  PlayIntegratedTransport,
} from '@/lib/play/playIntegratedTypes'
import type { ConnectShareTokenCreateResult } from '@/lib/play/useConnectShareTokenSession'

const PENDING_ACCEPT_ERROR =
  "We couldn't accept the connection. Please try again."
const PENDING_DECLINE_ERROR =
  "We couldn't decline the connection. Please try again."

const EMPTY_PENDING: PlayIntegratedPendingPayload = {
  success: true,
  pending: { inbound: [], outbound: [] },
  actionsAvailable: false,
}

type FetchPlayResult<T> =
  | { ok: true; data: T }
  | { ok: false; message: string; code: string; status: number }

async function fetchPlayJson<T>(
  url: string,
  fallbackMessage: string,
  init?: RequestInit
): Promise<FetchPlayResult<T>> {
  const response = await fetch(url, {
    ...init,
    credentials: 'include',
    cache: 'no-store',
  })
  const parsed = await readPlayApiJsonSafely<T>(response, fallbackMessage)
  if (!parsed.ok) {
    return {
      ok: false,
      message: parsed.message,
      code: parsed.code,
      status: parsed.status,
    }
  }
  return { ok: true, data: parsed.data }
}

function throwForFailedPlayReads(
  results: FetchPlayResult<unknown>[],
  mode: 'production' | 'preview'
): never {
  const dogAccessFailure = results.find(
    (r) => !r.ok && isPlayDogAccessErrorCode(r.code)
  )
  if (dogAccessFailure && !dogAccessFailure.ok) {
    throw createPlayLoadError(dogAccessFailure.code, mode)
  }
  throw createPlayLoadError(null, mode)
}

function buildDogQuery(
  activeDogId: string,
  extra?: Record<string, string>
): string {
  const params = new URLSearchParams({ dogId: activeDogId, ...extra })
  return `?${params.toString()}`
}

/**
 * Load tab-summary for first paint (includes relationships), then attach pending
 * details without blocking first meaningful content when pending is slow or fails.
 * Standalone /circle is not required for onCoreReady.
 */
async function loadIntegratedCoreThenPending(options: {
  mode: 'production' | 'preview'
  apiBase: string
  activeDogId: string
  fallback: string
  queryExtra?: Record<string, string>
  hooks?: PlayIntegratedLoadHooks
  coordinationFromSummary?: (
    summary: PlayIntegratedTabSummaryPayload
  ) => PlayIntegratedCoordinationPreview | null
}): Promise<PlayIntegratedLoadResult> {
  const {
    mode,
    apiBase,
    activeDogId,
    fallback,
    queryExtra,
    hooks,
    coordinationFromSummary,
  } = options
  const qs = buildDogQuery(activeDogId, queryExtra)

  markPlayStartup('tab_summary_request_start')
  const tabRequestStarted = performance.now()
  const tabRes = await fetchPlayJson<PlayIntegratedTabSummaryPayload>(
    `${apiBase}/tab-summary${qs}`,
    fallback
  )
  markPlayStartup(
    'tab_summary_rtt',
    `${Math.round(performance.now() - tabRequestStarted)}ms`
  )

  if (!tabRes.ok) {
    throwForFailedPlayReads([tabRes], mode)
  }

  markPlayStartup('tab_summary_json_received')
  markPlayStartupServerTimings(
    (tabRes.data as { timingsMs?: Record<string, number> }).timingsMs
  )

  let circlePayload = tabSummaryHasPaintableCircle(tabRes.data)
    ? circlePayloadFromTabSummary(tabRes.data)
    : null

  // Compatibility fallback if an older summary omits relationships.
  if (!circlePayload) {
    markPlayStartup('circle_fallback_request_start')
    const circleRes = await fetchPlayJson<PlayIntegratedCirclePayload>(
      `${apiBase}/circle${qs}`,
      fallback
    )
    if (!circleRes.ok) {
      throwForFailedPlayReads([circleRes], mode)
    }
    circlePayload = circleRes.data
  }

  const coordinationOverlay = coordinationFromSummary?.(tabRes.data) ?? null
  hooks?.onCoreReady?.({
    summary: tabRes.data,
    circle: circlePayload,
    coordinationOverlay,
  })
  markPlayStartup('core_ready_dispatched')

  markPlayStartup('pending_request_start')
  const pendingRes = await fetchPlayJson<PlayIntegratedPendingPayload>(
    `${apiBase}/circle/pending${qs}`,
    fallback
  )
  const pending = pendingRes.ok
    ? normalizePlayIntegratedPendingPayload(pendingRes.data)
    : EMPTY_PENDING
  markPlayStartup(
    pendingRes.ok ? 'pending_json_received' : 'pending_soft_failed'
  )

  return {
    summary: tabRes.data,
    circle: circlePayload,
    pending,
    coordinationOverlay,
  }
}

export function createPlayIntegratedProductionTransport(): PlayIntegratedTransport {
  const apiBase = PLAY_INTEGRATED_PRODUCTION_API_BASE
  const fallback = PLAY_LOAD_GENERIC_UNAVAILABLE

  return {
    mode: 'production',
    async loadData(
      activeDogId: string,
      hooks?: PlayIntegratedLoadHooks
    ): Promise<PlayIntegratedLoadResult> {
      return loadIntegratedCoreThenPending({
        mode: 'production',
        apiBase,
        activeDogId,
        fallback,
        hooks,
      })
    },

    async runPendingAction(
      connectionRef: string,
      action: 'accept' | 'decline',
      recipientDogId: string
    ): Promise<PlayIntegratedPendingActionResult> {
      // Canonical contract shared with Resume Accept (recipientDogId required).
      const { acceptConnectPendingAction, declineConnectPendingAction } =
        await import('@/lib/play/connectAcceptanceClient')
      const result =
        action === 'accept'
          ? await acceptConnectPendingAction(connectionRef, recipientDogId, {
              clearHandoff: false,
            })
          : await declineConnectPendingAction(connectionRef, recipientDogId, {
              clearHandoff: false,
            })
      if (!result.ok) {
        return {
          ok: false,
          message:
            result.message ??
            (action === 'accept'
              ? PENDING_ACCEPT_ERROR
              : PENDING_DECLINE_ERROR),
        }
      }
      return { ok: true }
    },

    async createShareToken(
      activeDogId: string
    ): Promise<ConnectShareTokenCreateResult> {
      const idempotencyKey =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `share-${Date.now()}-${Math.random().toString(36).slice(2)}`
      const response = await fetch(`${apiBase}/connect/share`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKey,
        },
        body: JSON.stringify({ senderDogId: activeDogId }),
      })
      return parseProductionShareResponse(response)
    },
  }
}

export type PlayIntegratedPreviewTransportOptions = {
  previewState: PlayIntegratedPreviewState
}

export function createPlayIntegratedPreviewTransport(
  options: PlayIntegratedPreviewTransportOptions
): PlayIntegratedTransport {
  const apiBase = DEV_PREVIEW_PLAY_INTEGRATED_API_BASE
  const { previewState } = options
  const fallback = PLAY_LOAD_PREVIEW_UNAVAILABLE

  return {
    mode: 'preview',
    async loadData(
      activeDogId: string,
      hooks?: PlayIntegratedLoadHooks
    ): Promise<PlayIntegratedLoadResult> {
      return loadIntegratedCoreThenPending({
        mode: 'preview',
        apiBase,
        activeDogId,
        fallback,
        queryExtra: { state: previewState },
        hooks,
        coordinationFromSummary: (summary) => {
          const preview = summary as PlayIntegratedPreviewTabSummaryPayload
          return preview.coordination ?? null
        },
      })
    },

    async runPendingAction(
      connectionRef: string,
      action: 'accept' | 'decline',
      recipientDogId: string
    ): Promise<PlayIntegratedPendingActionResult> {
      const response = await fetch(`${apiBase}/pending-action`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ connectionRef, action, recipientDogId }),
      })
      const parsed = await readPlayApiJsonSafely<{
        success: boolean
        error?: string
      }>(
        response,
        action === 'accept' ? PENDING_ACCEPT_ERROR : PENDING_DECLINE_ERROR
      )
      if (!parsed.ok || !parsed.data.success) {
        return {
          ok: false,
          message: parsed.ok
            ? (parsed.data.error ?? 'Request failed.')
            : parsed.message,
        }
      }
      return { ok: true }
    },

    async createShareToken(
      activeDogId: string
    ): Promise<ConnectShareTokenCreateResult> {
      const response = await fetch(
        `${apiBase}/share?state=${encodeURIComponent(previewState)}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ senderDogId: activeDogId }),
        }
      )
      return parsePreviewShareResponse(
        response,
        INTEGRATED_PREVIEW_SHARE_USER_MESSAGE
      )
    },
  }
}
